import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Customer } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as fromStrategy from '../../reducers';

import { ESTIMATE_CONSTANTS_CH, ESTIMATE_CONSTANTS_DE } from '@app/app.constants';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';
import { StackedAreaChartConfig, StackedAreaChartData, StackedAreaChartDataItem } from '@strategy/components';

@Component({
  selector: 'app-strategy-portfolio-estimated-development',
  templateUrl: './strategy-portfolio-estimated-development.component.html',
  styleUrls: ['./strategy-portfolio-estimated-development.component.scss'],
  standalone: false
})
export class StrategyPortfolioEstimatedDevelopmentComponent implements OnChanges, OnDestroy, AfterViewInit {
  protected readonly windowResize$ = new Subject<string>();
  protected readonly onDestroy$ = new Subject<void>();
  @Input() investmentData: CustomerInvestmentData;
  @Input() customer: Customer;
  @ViewChild('estimatedDevelopmentWrapper', { static: false }) estimatedDevelopmentWrapper: ElementRef;

  chartData: StackedAreaChartData;
  chartDataInvestedByYear: number[];
  chartConfig: StackedAreaChartConfig;
  calculatedData: StackedAreaChartDataItem[];

  showMore = false;
  yearsPicked = 5;
  riskName = 'balanced';
  expectedReturn: number;
  expectedReturnRatio: number;
  expectedPotentialRatio: number;
  expectedLossRatio: number;
  time = 20;

  after1: number;
  after5: number;
  after10: number;
  after15: number;
  after20: number;

  constructor(
    private store: Store,
    public router: Router,
    private trackingService: TrackingService,
    private cd: ChangeDetectorRef
  ) {
    // selectStateData

    this.store.pipe(select(fromStrategy.selectRiskName), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.riskName = value.riskName;
    });
    this.store.pipe(select(fromStrategy.selectInvestmentYear), takeUntil(this.onDestroy$)).subscribe((years) => {
      this.yearsPicked = years;
      this.calculateExpectedReturn(this.yearsPicked);
    });
    this.windowResize$.pipe(takeUntil(this.onDestroy$), debounceTime(1000)).subscribe(() => {
      this.calculateChartWidth();
    });
  }

  @HostListener('window:resize', ['$event']) onResize(event: { timeStamp: string }) {
    this.windowResize$.next(event.timeStamp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const investmentData: CustomerInvestmentData = changes.investmentData ? changes.investmentData.currentValue : {};
    if (!investmentData.total && !investmentData.monthly_investment) {
      return;
    }
    this.calculateChartData(investmentData);
    this.calculateExpectedReturn(this.yearsPicked);
  }

  ngAfterViewInit(): void {
    this.calculateChartWidth();
  }

  calculateChartData(investmentData: CustomerInvestmentData): void {
    // clear data
    this.chartData = { data: [], columns: ['date', 'badCase', 'expectedCase', 'goodCase'] };

    const { total } = investmentData;

    // Don't calculate data for customers with negative assets
    if (total && total < 0) {
      return;
    }

    const monthly = investmentData.monthly_investment;
    const stocksSplit = investmentData.stock_split;
    const { stocksReturn, bondsReturn, stocksVol, bondsVol, correlation, confidenceInterval } =
      this.customer.app_location !== 'ch' ? ESTIMATE_CONSTANTS_DE : ESTIMATE_CONSTANTS_CH;
    const cashReturn = 0;
    const cashRatio = 0.02;
    const actualStockSplit = stocksSplit * (1 - cashRatio);
    const actualBondSplit = 1 - actualStockSplit - cashRatio;

    const currentYear = new Date().getFullYear();
    const fees = 0;

    const expectedReturnRatio = actualStockSplit * stocksReturn + actualBondSplit * bondsReturn + cashRatio * cashReturn;

    const expectedSTDDevRatio = Math.sqrt(
      actualStockSplit ** 2 * stocksVol ** 2 +
        actualBondSplit ** 2 * bondsVol ** 2 +
        cashRatio ** 2 * cashReturn ** 2 +
        2 * actualStockSplit * actualBondSplit * stocksVol * bondsVol * correlation ** 2
    );

    const expectedSTDDevRatioFinal = Math.sqrt(Math.log(1 + expectedSTDDevRatio ** 2 / (1 + expectedReturnRatio) ** 2));
    const expectedReturnRatioFinal = Math.log(1 + expectedReturnRatio) - expectedSTDDevRatioFinal ** 2 / 2;
    const expectedMonthlyReturn = Math.exp(expectedReturnRatioFinal / 12 + (expectedSTDDevRatioFinal / Math.sqrt(12)) ** 2 / 2) - 1;

    this.expectedReturnRatio = expectedReturnRatio;
    this.expectedPotentialRatio = Math.exp(expectedReturnRatioFinal + confidenceInterval * expectedSTDDevRatioFinal) - 1;
    this.expectedLossRatio = Math.exp(expectedReturnRatioFinal - confidenceInterval * expectedSTDDevRatioFinal) - 1;

    let NAV = total;
    let totalInvestedByYear = total;

    this.chartDataInvestedByYear = [totalInvestedByYear];
    this.chartData.data.push({
      date: new Date(`01/01/${currentYear.toFixed()}`),
      badCase: NAV,
      expectedCase: 0,
      goodCase: 0
    });

    for (let i = 1; i <= this.time * 12; i++) {
      const aggregatedMonthlyReturn = Math.exp((expectedReturnRatioFinal / 12 + (expectedSTDDevRatioFinal / Math.sqrt(12)) ** 2 / 2) * i) * 1;

      const returnInPeriod = parseFloat((NAV * expectedMonthlyReturn * 1).toFixed(2));
      const NAVbeforeFees = NAV + parseFloat(returnInPeriod.toFixed(2));
      const NAVafterFees = NAVbeforeFees + fees;

      totalInvestedByYear += monthly;
      NAV += monthly + returnInPeriod;

      const worstCaseReturn = Math.exp(i * (expectedReturnRatioFinal / 12 - (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * i)));

      const WCNav = (NAVafterFees / aggregatedMonthlyReturn) * worstCaseReturn;

      const bestCaseReturn = Math.exp(expectedReturnRatioFinal / 12 + (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * i)) ** i;

      const BCNAv = (NAVafterFees / aggregatedMonthlyReturn) * bestCaseReturn;

      if (i % 12 === 0) {
        this.chartData.data.push({
          date: new Date(`01/01/${(currentYear + i / 12).toFixed()}`),
          badCase: parseFloat(WCNav.toFixed(0)),
          expectedCase: parseFloat(NAV.toFixed(0)) - parseFloat(WCNav.toFixed(0)),
          goodCase: parseFloat(BCNAv.toFixed(0)) - parseFloat(NAV.toFixed(0))
        });
        this.chartDataInvestedByYear.push(totalInvestedByYear);
      }

      this.calculatedData = [...this.chartData.data];

      switch (i) {
        case 1 * 12:
          this.after1 = NAV;
          break;
        case 5 * 12:
          this.after5 = NAV;
          break;
        case 10 * 12:
          this.after10 = NAV;
          break;
        case 15 * 12:
          this.after15 = NAV;
          break;
        case 20 * 12:
          this.after20 = NAV;
          break;
        default:
          this.after20 = NAV;
          break;
      }
    }
  }

  calculateExpectedReturn(time: number | string): void {
    if (this.calculateChartData?.length && this.chartData?.data?.length) {
      this.chartData = {
        columns: [...this.chartData.columns],
        data: [...this.calculatedData.slice(0, Number(time) + 1)]
      };
    }

    switch (Number(time)) {
      case 1:
        this.expectedReturn = this.after1;
        break;
      case 5:
        this.expectedReturn = this.after5;
        break;
      case 10:
        this.expectedReturn = this.after10;
        break;
      case 15:
        this.expectedReturn = this.after15;
        break;
      case 20:
        this.expectedReturn = this.after20;
        break;
      default:
        this.expectedReturn = this.after20;
        break;
    }
  }

  toggleShowMore(showMore: boolean) {
    this.showMore = showMore;
    this.trackActivity(`[Button] Estimated assets: Show ${this.showMore ? 'more' : 'less'}`);
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private calculateChartWidth(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const containerWidth = this.estimatedDevelopmentWrapper?.nativeElement?.offsetWidth;

    this.chartConfig = {
      height: 250,
      width: containerWidth ? containerWidth - 40 : 520,
      margin: {
        top: 20,
        bottom: 20,
        left: 15,
        right: 10
      }
    };

    this.cd.detectChanges();
  }
}
